import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatPrice, toImg } from "@/api/client";
import ContactForm from "@/components/ContactForm";
import SEO, { propertySchema, breadcrumbSchema } from "@/components/SEO";
import VideoEmbed from "@/components/VideoEmbed";
import DroneEmbed from "@/components/DroneEmbed";
import MapEmbed from "@/components/MapEmbed";
import FavoriteButton from "@/components/FavoriteButton";
import Lightbox from "@/components/Lightbox";
import BookingWidget from "@/components/BookingWidget";
import NeighborhoodInsights from "@/components/NeighborhoodInsights";
import { MapPin, Ruler, Home as HomeIcon, CheckCircle2, Maximize2, Building2, Tag, Layers, BedDouble, Hash } from "lucide-react";

export default function ImovelDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [lightboxIdx, setLightboxIdx] = useState(null);

  useEffect(() => {
    setItem(null);
    setNotFound(false);
    api.get(`/properties/${slug}`).then((r) => setItem(r.data)).catch(() => setNotFound(true));
  }, [slug]);

  if (notFound) {
    return (
      <div className="container-cnp py-24 text-center">
        <h1 className="font-display text-3xl">Imóvel não encontrado</h1>
        <Link to="/imoveis" className="btn-secondary mt-6 inline-flex">Ver todos os imóveis</Link>
      </div>
    );
  }
  if (!item) return <div className="container-cnp py-24">A carregar...</div>;

  const gallery = item.gallery && item.gallery.length > 0 ? item.gallery : [item.main_image];
  const canonical = `https://casanoporto.com/imoveis/${item.slug}`;

  return (
    <div data-testid={`page-imovel-${item.slug}`}>
      <SEO
        title={`${item.title} — ${formatPrice(item.price, item.currency)}`}
        description={item.short_description || item.full_description?.slice(0, 160)}
        canonical={canonical}
        image={toImg(item.main_image)}
        jsonLd={[
          propertySchema(item, canonical),
          breadcrumbSchema([
            { name: "Início", url: "https://casanoporto.com/" },
            { name: "Imóveis", url: "https://casanoporto.com/imoveis" },
            { name: item.title, url: canonical },
          ]),
        ]}
      />
      <section className="container-cnp pt-8 lg:pt-12">
        <Link to="/imoveis" className="text-sm text-stone-500 hover:text-[#103C4A]">← Voltar aos imóveis</Link>
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <span className="tag-chip">{item.property_type}</span>
          <span className="tag-chip">{item.typology}</span>
          {item.status !== "à venda" && <span className="tag-chip bg-[#103C4A] text-white">{item.status}</span>}
        </div>
        <h1 className="mt-4 font-display text-3xl md:text-5xl text-stone-900 leading-tight">{item.title}</h1>
        <div className="mt-2 flex items-center gap-2 text-stone-500"><MapPin className="h-4 w-4" /> {item.locality}</div>
      </section>

      <section className="container-cnp pt-8 grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-8">
          {/* Masonry gallery */}
          <div className="grid grid-cols-4 grid-rows-2 gap-3 h-[520px]" data-testid="property-gallery">
            {gallery.slice(0, 4).map((g, i) => (
              <button
                key={i}
                type="button"
                onClick={() => setLightboxIdx(i)}
                data-testid={`gallery-tile-${i}`}
                className={`group relative overflow-hidden rounded-2xl bg-stone-100 ${
                  i === 0 ? "col-span-4 row-span-2 md:col-span-2 md:row-span-2" : "col-span-2 md:col-span-1"
                }`}
              >
                <img src={toImg(g)} alt={`${item.title} — foto ${i + 1}`} loading="lazy" className="h-full w-full object-cover transition-transform duration-[700ms] group-hover:scale-105" />
                {i === 3 && gallery.length > 4 && (
                  <div className="absolute inset-0 bg-stone-900/60 flex items-center justify-center text-white text-lg font-medium">+{gallery.length - 4}</div>
                )}
                {i === 0 && (
                  <div className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-white/90 px-3 py-1.5 text-xs text-stone-800 shadow">
                    <Maximize2 className="h-3.5 w-3.5" /> Ver galeria
                  </div>
                )}
              </button>
            ))}
          </div>
        </div>
        <aside className="lg:col-span-4">
          <div className="card-soft p-6 sticky top-24">
            <div className="text-xs uppercase tracking-widest text-stone-500">Preço</div>
            <div className="mt-1 font-display text-4xl text-[#103C4A]">{formatPrice(item.price, item.currency)}</div>
            <div className="mt-4 grid grid-cols-3 gap-3 text-center">
              <div className="rounded-lg bg-stone-50 p-3">
                <div className="text-xs text-stone-500">Tipologia</div>
                <div className="text-sm font-medium">{item.typology}</div>
              </div>
              <div className="rounded-lg bg-stone-50 p-3">
                <div className="text-xs text-stone-500">Área</div>
                <div className="text-sm font-medium">{item.area || "—"}{item.area ? "m²" : ""}</div>
              </div>
              <div className="rounded-lg bg-stone-50 p-3">
                <div className="text-xs text-stone-500">Tipo</div>
                <div className="text-sm font-medium">{item.property_type}</div>
              </div>
            </div>
            <div className="mt-6">
              <ContactForm leadType="comprador" propertySlug={item.slug} zone={item.locality} compact testidPrefix={`imovel-${item.slug}-contact`} />
            </div>
          </div>
        </aside>
      </section>

      <section className="container-cnp py-16 lg:py-24 grid lg:grid-cols-12 gap-10 lg:gap-14">
        <div className="lg:col-span-8">
          <div className="mb-4 flex items-baseline justify-between gap-6">
            <h2 className="font-display text-3xl md:text-4xl text-stone-900 tracking-[-0.02em]">Sobre o imóvel</h2>
            <span className="text-[10px] uppercase tracking-[0.24em] text-stone-400">o imóvel</span>
          </div>
          <div className="h-px w-full bg-stone-200 mb-6" />
          <p className="text-stone-700 leading-relaxed whitespace-pre-wrap text-[15px] md:text-base">{item.full_description || item.short_description}</p>

          {/* Bento box quick facts */}
          <div className="mt-10 grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="bento-facts">
            <div className="card-soft p-5 col-span-2">
              <div className="text-[10px] uppercase tracking-[0.2em] text-stone-500">Preço</div>
              <div className="mt-2 font-display text-3xl text-[#103C4A]">{formatPrice(item.price, item.currency)}</div>
              {item.property_price_sqm ? (
                <div className="mt-1 text-xs text-stone-500">
                  {formatPrice(item.property_price_sqm, item.currency)} / m²
                  {item.zone_target_sqm ? (
                    <>
                      {" · "}
                      <span className={item.market_flag ? "text-[#103C4A] font-medium" : "text-stone-500"}>
                        média {item.typology} na zona: {formatPrice(item.zone_target_sqm, item.currency)}/m²
                      </span>
                    </>
                  ) : null}
                </div>
              ) : null}
            </div>
            {item.reference && (
              <div className="card-soft p-5" data-testid="bento-reference">
                <Hash className="h-4 w-4 text-[#103C4A]" />
                <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">Referência</div>
                <div className="mt-1 font-display text-2xl text-stone-900 break-all">{item.reference}</div>
              </div>
            )}
            <div className="card-soft p-5">
              <Ruler className="h-4 w-4 text-[#103C4A]" />
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">Área</div>
              <div className="mt-1 font-display text-2xl text-stone-900">{item.area ? `${item.area}m²` : "—"}</div>
            </div>
            <div className="card-soft p-5">
              <BedDouble className="h-4 w-4 text-[#103C4A]" />
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">Tipologia</div>
              <div className="mt-1 font-display text-2xl text-stone-900">{item.typology}</div>
            </div>
            <div className="card-soft p-5">
              <Building2 className="h-4 w-4 text-[#103C4A]" />
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">Tipo</div>
              <div className="mt-1 font-display text-lg text-stone-900">{item.property_type}</div>
            </div>
            <div className="card-soft p-5">
              <Tag className="h-4 w-4 text-[#103C4A]" />
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">Estado</div>
              <div className="mt-1 font-display text-lg text-stone-900 capitalize">{item.status}</div>
            </div>
            <div className="card-soft p-5 col-span-2">
              <MapPin className="h-4 w-4 text-[#103C4A]" />
              <div className="mt-3 text-[10px] uppercase tracking-[0.2em] text-stone-500">Localização</div>
              <div className="mt-1 font-display text-lg text-stone-900 leading-tight">{item.locality}</div>
            </div>
          </div>

          {item.video_url && (
            <div className="mt-12">
              <div className="mb-4 flex items-baseline justify-between gap-6">
                <h3 className="font-display text-2xl md:text-3xl text-stone-900 tracking-[-0.02em]">Visita em vídeo</h3>
                <span className="text-[10px] uppercase tracking-[0.24em] text-stone-400">01</span>
              </div>
              <VideoEmbed url={item.video_url} title={item.title} />
            </div>
          )}

          {item.drone_url && (
            <div className="mt-12" data-testid="drone-section">
              <div className="mb-4 flex items-baseline justify-between gap-6">
                <h3 className="font-display text-2xl md:text-3xl text-stone-900 tracking-[-0.02em]">Vista aérea &amp; envolvente</h3>
                <span className="text-[10px] uppercase tracking-[0.24em] text-stone-400">{item.video_url ? "02" : "01"}</span>
              </div>
              <DroneEmbed url={item.drone_url} title={`${item.title} — vista aérea`} />
              <p className="mt-2 text-sm text-stone-500">Explore a envolvente do imóvel — perspetiva aérea ou Google Street View.</p>
            </div>
          )}

          {(item.latitude != null || item.longitude != null || item.locality) && (
            <div className="mt-10" data-testid="location-section">
              <h3 className="font-display text-xl md:text-2xl text-stone-900 mb-4">Localização</h3>
              <MapEmbed lat={item.latitude} lng={item.longitude} address={item.locality} />
              <p className="mt-2 text-sm text-stone-500">Mapa aproximado. A morada exata é partilhada em contacto direto.</p>
            </div>
          )}

          <NeighborhoodInsights lat={item.latitude} lon={item.longitude} radius={1000} />

          {item.features?.length > 0 && (
            <div className="mt-10">
              <h3 className="font-display text-xl md:text-2xl text-stone-900">Características</h3>
              <div className="mt-4 grid sm:grid-cols-2 gap-2">
                {item.features.map((f, i) => (
                  <div key={i} className="flex items-center gap-2 text-stone-700 py-1.5"><CheckCircle2 className="h-4 w-4 text-[#103C4A] shrink-0" /> {f}</div>
                ))}
              </div>
            </div>
          )}
        </div>
        <div className="lg:col-span-4">
          <div className="sticky top-24">
            <BookingWidget propertySlug={item.slug} propertyTitle={item.title} locality={item.locality} />
          </div>
        </div>
      </section>

      <Lightbox images={gallery} openIndex={lightboxIdx} onClose={() => setLightboxIdx(null)} alt={item.title} />
    </div>
  );
}
